import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  EyeIcon,
  SwatchIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

import type { 
  GraficoSpiderContent, 
  EixoGraficoConfig, 
  EstiloGraficoConfig 
} from '../../services/contentSnapshotService';
import type { ValidationError } from '../../services/contentAdminService';

interface SpiderChartEditorProps {
  versionId: string;
  content: any;
  onChange: (content: any) => void;
  validationErrors: ValidationError[];
}

interface AxisForm {
  id: string;
  nome: string;
  descricao: string;
  posicao_graus: number;
  dimensao_vinculada: string;
  cor: string;
}

const defaultColors = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
];

export const SpiderChartEditor: React.FC<SpiderChartEditorProps> = ({
  versionId,
  content,
  onChange,
  validationErrors,
}) => {
  const [editingAxis, setEditingAxis] = useState<string | null>(null);
  const [axisForm, setAxisForm] = useState<AxisForm>({
    id: '',
    nome: '',
    descricao: '',
    posicao_graus: 0,
    dimensao_vinculada: '',
    cor: defaultColors[0],
  });
  const [showPreview, setShowPreview] = useState(false);

  const spiderChart: GraficoSpiderContent = content.spider_chart || {
    versao: '1.0',
    data_criacao: new Date().toISOString(),
    descricao: 'Configuração do gráfico spider para visualização de perfis',
    eixos: [],
    estilo: {
      raio_maximo: 100,
      cor_linha_grade: '#E5E7EB',
      cor_linha_perfil: '#3B82F6',
      espessura_linha_perfil: 2,
      cor_preenchimento: 'rgba(59, 130, 246, 0.1)',
      mostrar_valores: true,
      mostrar_labels: true,
      fonte_labels: 'Inter',
      tamanho_fonte_labels: 12,
    },
    configuracao_visual: {
      largura: 400,
      altura: 400,
      margem: 50,
      animacao_duracao: 1000,
      gradiente_ativo: true,
    },
  };

  const updateSpiderChart = (newChart: GraficoSpiderContent) => {
    onChange({
      ...content,
      spider_chart: newChart,
    });
  };

  // Get available dimensions from questions content
  const availableDimensions = content.questions?.dimensoes || [];

  // Axis management
  const handleAddAxis = () => {
    const newAxisId = `eixo_${Date.now()}`;
    const nextPosition = spiderChart.eixos.length * (360 / Math.max(1, availableDimensions.length));
    
    setAxisForm({
      id: newAxisId,
      nome: 'Novo Eixo',
      descricao: 'Descrição do eixo',
      posicao_graus: nextPosition,
      dimensao_vinculada: availableDimensions[0]?.id || '',
      cor: defaultColors[spiderChart.eixos.length % defaultColors.length],
    });
    setEditingAxis(newAxisId);
  };

  const handleEditAxis = (axis: EixoGraficoConfig) => {
    setAxisForm({
      id: axis.id,
      nome: axis.nome,
      descricao: axis.descricao,
      posicao_graus: axis.posicao_graus,
      dimensao_vinculada: axis.dimensao_vinculada,
      cor: axis.cor,
    });
    setEditingAxis(axis.id);
  };

  const handleSaveAxis = () => {
    if (!axisForm.nome.trim()) return;

    const existingIndex = spiderChart.eixos.findIndex(a => a.id === axisForm.id);
    let newAxes = [...spiderChart.eixos];

    if (existingIndex >= 0) {
      newAxes[existingIndex] = axisForm;
    } else {
      newAxes.push(axisForm);
    }

    updateSpiderChart({
      ...spiderChart,
      eixos: newAxes.sort((a, b) => a.posicao_graus - b.posicao_graus),
    });

    setEditingAxis(null);
    setAxisForm({
      id: '',
      nome: '',
      descricao: '',
      posicao_graus: 0,
      dimensao_vinculada: '',
      cor: defaultColors[0],
    });
  };

  const handleDeleteAxis = (axisId: string) => {
    const newAxes = spiderChart.eixos.filter(a => a.id !== axisId);
    updateSpiderChart({
      ...spiderChart,
      eixos: newAxes,
    });
  };

  const handleAutoArrangeAxes = () => {
    const angleStep = 360 / spiderChart.eixos.length;
    const arrangedAxes = spiderChart.eixos.map((axis, index) => ({
      ...axis,
      posicao_graus: index * angleStep,
    }));

    updateSpiderChart({
      ...spiderChart,
      eixos: arrangedAxes,
    });
  };

  const handleSyncWithDimensions = () => {
    const newAxes = availableDimensions.map((dimension, index) => {
      const existingAxis = spiderChart.eixos.find(a => a.dimensao_vinculada === dimension.id);
      const angleStep = 360 / availableDimensions.length;
      
      return existingAxis || {
        id: `eixo_${dimension.id}`,
        nome: dimension.nome,
        descricao: dimension.descricao,
        posicao_graus: index * angleStep,
        dimensao_vinculada: dimension.id,
        cor: defaultColors[index % defaultColors.length],
      };
    });

    updateSpiderChart({
      ...spiderChart,
      eixos: newAxes,
    });
  };

  const handleUpdateStyle = (field: string, value: any) => {
    if (field.includes('configuracao_visual.')) {
      const visualField = field.split('.')[1];
      updateSpiderChart({
        ...spiderChart,
        configuracao_visual: {
          ...spiderChart.configuracao_visual,
          [visualField]: value,
        },
      });
    } else {
      updateSpiderChart({
        ...spiderChart,
        estilo: {
          ...spiderChart.estilo,
          [field]: value,
        },
      });
    }
  };

  const handleUpdateBasicInfo = (field: string, value: string) => {
    updateSpiderChart({
      ...spiderChart,
      [field]: value,
    });
  };

  const getValidationErrors = (field: string) => {
    return validationErrors.filter(error => error.field.includes(field));
  };

  // Simple preview component
  const SpiderChartPreview = () => {
    const size = 200;
    const center = size / 2;
    const radius = 80;

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Grid circles */}
          {[0.2, 0.4, 0.6, 0.8, 1].map((scale, index) => (
            <circle
              key={index}
              cx={center}
              cy={center}
              r={radius * scale}
              fill="none"
              stroke={spiderChart.estilo.cor_linha_grade}
              strokeWidth="1"
            />
          ))}
          
          {/* Axes */}
          {spiderChart.eixos.map((axis, index) => {
            const angle = (axis.posicao_graus - 90) * (Math.PI / 180);
            const x = center + Math.cos(angle) * radius;
            const y = center + Math.sin(angle) * radius;
            
            return (
              <g key={axis.id}>
                <line
                  x1={center}
                  y1={center}
                  x2={x}
                  y2={y}
                  stroke={spiderChart.estilo.cor_linha_grade}
                  strokeWidth="1"
                />
                {spiderChart.estilo.mostrar_labels && (
                  <text
                    x={x + Math.cos(angle) * 15}
                    y={y + Math.sin(angle) * 15}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={spiderChart.estilo.tamanho_fonte_labels}
                    fill={axis.cor}
                    className="font-medium"
                  >
                    {axis.nome}
                  </text>
                )}
              </g>
            );
          })}
          
          {/* Sample data visualization */}
          {spiderChart.eixos.length > 0 && (
            <polygon
              points={spiderChart.eixos.map((axis, index) => {
                const sampleValue = 0.7; // Sample value
                const angle = (axis.posicao_graus - 90) * (Math.PI / 180);
                const x = center + Math.cos(angle) * radius * sampleValue;
                const y = center + Math.sin(angle) * radius * sampleValue;
                return `${x},${y}`;
              }).join(' ')}
              fill={spiderChart.estilo.cor_preenchimento}
              stroke={spiderChart.estilo.cor_linha_perfil}
              strokeWidth={spiderChart.estilo.espessura_linha_perfil}
            />
          )}
        </svg>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Basic Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Configuração do Gráfico Spider
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Versão
            </label>
            <input
              type="text"
              value={spiderChart.versao}
              onChange={(e) => handleUpdateBasicInfo('versao', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
              {showPreview ? 'Ocultar' : 'Mostrar'} Preview
            </button>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={spiderChart.descricao}
              onChange={(e) => handleUpdateBasicInfo('descricao', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Preview */}
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-gray-200"
            >
              <h4 className="text-md font-medium text-gray-900 mb-4">Preview do Gráfico</h4>
              <SpiderChartPreview />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Axes Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Eixos do Gráfico ({spiderChart.eixos.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure os eixos e suas posições no gráfico spider
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {availableDimensions.length > 0 && (
              <button
                onClick={handleSyncWithDimensions}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                Sincronizar
              </button>
            )}
            
            {spiderChart.eixos.length > 1 && (
              <button
                onClick={handleAutoArrangeAxes}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SwatchIcon className="w-4 h-4" />
                Auto-organizar
              </button>
            )}

            <button
              onClick={handleAddAxis}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Novo Eixo
            </button>
          </div>
        </div>

        {/* Axis Editor Form */}
        <AnimatePresence>
          {editingAxis && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 border border-blue-200 rounded-lg p-4 bg-blue-50"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Eixo
                    </label>
                    <input
                      type="text"
                      value={axisForm.nome}
                      onChange={(e) => setAxisForm({ ...axisForm, nome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimensão Vinculada
                    </label>
                    <select
                      value={axisForm.dimensao_vinculada}
                      onChange={(e) => setAxisForm({ ...axisForm, dimensao_vinculada: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione uma dimensão</option>
                      {availableDimensions.map((dim) => (
                        <option key={dim.id} value={dim.id}>
                          {dim.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Posição (graus)
                    </label>
                    <input
                      type="number"
                      value={axisForm.posicao_graus}
                      onChange={(e) => setAxisForm({ ...axisForm, posicao_graus: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="359"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cor
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={axisForm.cor}
                        onChange={(e) => setAxisForm({ ...axisForm, cor: e.target.value })}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={axisForm.cor}
                        onChange={(e) => setAxisForm({ ...axisForm, cor: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={axisForm.descricao}
                    onChange={(e) => setAxisForm({ ...axisForm, descricao: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setEditingAxis(null);
                      setAxisForm({
                        id: '',
                        nome: '',
                        descricao: '',
                        posicao_graus: 0,
                        dimensao_vinculada: '',
                        cor: defaultColors[0],
                      });
                    }}
                    className="px-3 py-1.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 inline mr-1" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveAxis}
                    disabled={!axisForm.nome.trim()}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckIcon className="w-4 h-4 inline mr-1" />
                    Salvar
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Axes List */}
        <div className="space-y-3">
          {spiderChart.eixos.map((axis, index) => (
            <div key={axis.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: axis.cor }}
                  ></div>
                  <div>
                    <h4 className="font-medium text-gray-900">{axis.nome}</h4>
                    <p className="text-sm text-gray-600">
                      {axis.posicao_graus}° • {availableDimensions.find(d => d.id === axis.dimensao_vinculada)?.nome || axis.dimensao_vinculada}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEditAxis(axis)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteAxis(axis.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {spiderChart.eixos.length === 0 && (
          <div className="text-center py-12">
            <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum eixo configurado
            </h3>
            <p className="text-gray-600 mb-4">
              Adicione eixos para configurar a visualização do gráfico spider.
            </p>
            <div className="flex justify-center gap-3">
              {availableDimensions.length > 0 && (
                <button
                  onClick={handleSyncWithDimensions}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <AdjustmentsHorizontalIcon className="w-4 h-4" />
                  Sincronizar com Dimensões
                </button>
              )}
              <button
                onClick={handleAddAxis}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Adicionar Primeiro Eixo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Visual Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Configurações Visuais
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Largura (px)
            </label>
            <input
              type="number"
              value={spiderChart.configuracao_visual.largura}
              onChange={(e) => handleUpdateStyle('configuracao_visual.largura', parseInt(e.target.value) || 400)}
              min="200"
              max="800"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Altura (px)
            </label>
            <input
              type="number"
              value={spiderChart.configuracao_visual.altura}
              onChange={(e) => handleUpdateStyle('configuracao_visual.altura', parseInt(e.target.value) || 400)}
              min="200"
              max="800"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Margem (px)
            </label>
            <input
              type="number"
              value={spiderChart.configuracao_visual.margem}
              onChange={(e) => handleUpdateStyle('configuracao_visual.margem', parseInt(e.target.value) || 50)}
              min="20"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cor da Grade
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={spiderChart.estilo.cor_linha_grade}
                onChange={(e) => handleUpdateStyle('cor_linha_grade', e.target.value)}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={spiderChart.estilo.cor_linha_grade}
                onChange={(e) => handleUpdateStyle('cor_linha_grade', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cor da Linha do Perfil
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={spiderChart.estilo.cor_linha_perfil}
                onChange={(e) => handleUpdateStyle('cor_linha_perfil', e.target.value)}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={spiderChart.estilo.cor_linha_perfil}
                onChange={(e) => handleUpdateStyle('cor_linha_perfil', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Espessura da Linha
            </label>
            <input
              type="number"
              value={spiderChart.estilo.espessura_linha_perfil}
              onChange={(e) => handleUpdateStyle('espessura_linha_perfil', parseInt(e.target.value) || 2)}
              min="1"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tamanho da Fonte
            </label>
            <input
              type="number"
              value={spiderChart.estilo.tamanho_fonte_labels}
              onChange={(e) => handleUpdateStyle('tamanho_fonte_labels', parseInt(e.target.value) || 12)}
              min="8"
              max="24"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duração da Animação (ms)
            </label>
            <input
              type="number"
              value={spiderChart.configuracao_visual.animacao_duracao}
              onChange={(e) => handleUpdateStyle('configuracao_visual.animacao_duracao', parseInt(e.target.value) || 1000)}
              min="0"
              max="3000"
              step="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Checkboxes */}
        <div className="mt-6 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={spiderChart.estilo.mostrar_valores}
              onChange={(e) => handleUpdateStyle('mostrar_valores', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Mostrar valores numéricos</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={spiderChart.estilo.mostrar_labels}
              onChange={(e) => handleUpdateStyle('mostrar_labels', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Mostrar labels dos eixos</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={spiderChart.configuracao_visual.gradiente_ativo}
              onChange={(e) => handleUpdateStyle('configuracao_visual.gradiente_ativo', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Usar gradiente no preenchimento</span>
          </label>
        </div>
      </div>

      {/* Validation Errors */}
      {getValidationErrors('spider').length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Erros de Validação:
          </h4>
          <ul className="text-sm text-red-700 space-y-1">
            {getValidationErrors('spider').map((error, index) => (
              <li key={index}>• {error.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};